import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import { useEffect, useRef } from 'react'
import { coerceFrequentPeriod } from './steps/limits/periodFormat'
import type { WizardState } from './WizardContext'

/**
 * Hydrates wizard fields from the saved config exactly once, when the
 * config first becomes available.
 *
 * Per-step save means the API always has the latest user input — but if
 * RTK Query refetches mid-edit (after a mutation, after focus regain,
 * after a poll), a re-hydration here would clobber whatever the user is
 * currently typing. The hasHydratedRef gate prevents that.
 */
export function useWizardHydration(
  update: (partial: Partial<WizardState>) => void
) {
  const { data: config } = useGetRecoveryConfigQuery()
  const hasHydratedRef = useRef(false)

  useEffect(() => {
    if (!config || hasHydratedRef.current) return

    const partial: Partial<WizardState> = {}

    // AI Identity
    if (config.assistant_name != null) {
      partial.assistantName = config.assistant_name
    }
    if (config.identity_mode != null) {
      partial.identityMode = config.identity_mode
    }
    if (config.custom_introduction != null) {
      partial.customIntroduction = config.custom_introduction
    }

    // Operating mode (no UI yet — hydrate to preserve on submit)
    if (config.operating_mode) {
      partial.operatingMode = config.operating_mode
    }

    // AI-580: mark a re-entry so Finalize preserves (not overwrites) launch-time
    // fields with no wizard UI. Only set on a truly onboarded config — a
    // first-time merchant leaves it at the default (false).
    if (config.onboarding_completed) {
      partial.alreadyOnboarded = true
    }

    // Philosophy. A persisted posture only exists once the philosophy step
    // has been saved, so it represents a prior pick — mark it overridden so
    // re-running the assessment in a later session re-RECOMMENDS (the badge,
    // via recommendedPosture below) but never silently re-APPLIES over the
    // saved choice. Within-session, the flag is set by onSelect instead.
    // (AI-29 review — sticky-pick semantics across sessions.)
    if (config.philosophy_posture) {
      partial.philosophyPosture = config.philosophy_posture
      partial.philosophyPostureOverridden = true
    }
    // The wizard saves the recommendation alongside the posture, so restore
    // it for the "Recommended for you" badge on re-entry.
    if (config.philosophy_recommended_posture) {
      partial.recommendedPosture = config.philosophy_recommended_posture
    }

    // Limits & Guardrails
    if (config.default_daily_budget_cents != null) {
      partial.dailyBudget = String(config.default_daily_budget_cents / 100)
    }
    if (config.approved_reward_ids != null) {
      partial.selectedRewardIds = config.approved_reward_ids
    }
    if (config.frequent_complaint_threshold != null) {
      partial.frequentThreshold = String(config.frequent_complaint_threshold)
    }
    if (config.frequent_complaint_period) {
      // Coerce an out-of-vocabulary value (e.g. the backend's legacy '30_days',
      // once AI-438 exposes the column) to a valid display token so the wizard
      // period <select> never renders blank.
      partial.frequencyPeriod = coerceFrequentPeriod(
        config.frequent_complaint_period
      )
    }
    if (config.repeated_complaint_threshold != null) {
      partial.repeatedThreshold = String(config.repeated_complaint_threshold)
    }
    // Escalation recipients are NOT hydrated here any more (D1): they live on
    // the escalation_contacts resource, which the step reads directly. Same for
    // the location-managers toggle, which the step now writes eagerly — a
    // one-shot hydration into wizard state would clobber the merchant's
    // just-toggled choice when Finalize re-sent it.
    if (config.notify_admins != null) {
      partial.notifyAdmins = config.notify_admins
    }

    if (Object.keys(partial).length > 0) {
      update(partial)
    }
    hasHydratedRef.current = true
  }, [config, update])
}
